import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Calendar, Clock, Activity, RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'

interface TimeRange {
  label: string
  value: string
  days: number
}

interface HeatmapData {
  day: string
  hour: number
  requests: number
  intensity: number // 0-1 scale for color intensity
}

interface UsageHeatmapProps {
  timeRange: TimeRange
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const HOURS = Array.from({ length: 24 }, (_, i) => i)

export function UsageHeatmap({ timeRange }: UsageHeatmapProps) {
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchUsageHeatmap()
  }, [timeRange])

  const fetchUsageHeatmap = async () => {
    setLoading(true)
    try {
      // Pull usage logs across all user servers and bucket into day/hour bins
      const end = new Date()
      const start = new Date(end.getTime() - timeRange.days * 24 * 60 * 60 * 1000)
      const params = {
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        limit: 5000, // fetch a large sample window
      }
      const res = await api.get<{ entries: Array<any> }>('/logs/usage', params)
      const entries = Array.isArray(res?.entries) ? res.entries : []

      const buckets = new Map<string, number>()
      for (const e of entries) {
        const ts = e.timestamp ? new Date(e.timestamp) : null
        if (!ts) continue
        const day = DAYS[ts.getDay()]
        const hour = ts.getHours()
        const key = `${day}-${hour}`
        buckets.set(key, (buckets.get(key) || 0) + 1)
      }

      const data: HeatmapData[] = []
      DAYS.forEach((day) => {
        HOURS.forEach((hour) => {
          const key = `${day}-${hour}`
          const requests = buckets.get(key) || 0
          data.push({ day, hour, requests, intensity: 0 })
        })
      })

      const maxRequests = Math.max(...data.map((d) => d.requests))
      const minRequests = Math.min(...data.map((d) => d.requests))
      const range = maxRequests - minRequests
      data.forEach((item) => {
        item.intensity = range > 0 ? (item.requests - minRequests) / range : 0
      })

      setHeatmapData(data)
    } catch (err) {
      console.error('Failed to fetch usage heatmap:', err)
      setHeatmapData([])
    } finally {
      setLoading(false)
    }
  }

  const getIntensityColor = (intensity: number) => {
    // Blue color scale from light to dark
    const opacity = Math.max(0.1, intensity)
    return `rgba(59, 130, 246, ${opacity})`
  }

  const getIntensityLevel = (intensity: number) => {
    if (intensity >= 0.8) return { level: 'Very High', color: 'text-blue-800' }
    if (intensity >= 0.6) return { level: 'High', color: 'text-blue-700' }
    if (intensity >= 0.4) return { level: 'Medium', color: 'text-blue-600' }
    if (intensity >= 0.2) return { level: 'Low', color: 'text-blue-500' }
    return { level: 'Very Low', color: 'text-blue-400' }
  }

  const getTotalRequests = () => {
    return heatmapData.reduce((sum, item) => sum + item.requests, 0)
  }

  const getPeakHour = () => {
    if (heatmapData.length === 0) return null
    const peak = heatmapData.reduce((max, item) => 
      item.requests > max.requests ? item : max
    )
    return `${peak.day} at ${peak.hour}:00`
  }

  const getAverageByDay = () => {
    const dayTotals = DAYS.map(day => {
      const dayData = heatmapData.filter(item => item.day === day)
      const total = dayData.reduce((sum, item) => sum + item.requests, 0)
      return { day, total, average: total / dayData.length }
    })
    return dayTotals.sort((a, b) => b.total - a.total)
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Usage Patterns</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500 mr-2" />
            <span className="text-muted-foreground">Loading usage patterns...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Heatmap */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Usage Heatmap</span>
          </CardTitle>
          <CardDescription>
            Request patterns by day and hour for {timeRange.label.toLowerCase()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Legend */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Traffic Intensity</span>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-muted-foreground">Low</span>
                <div className="flex space-x-1">
                  {[0.1, 0.3, 0.5, 0.7, 0.9].map((intensity, i) => (
                    <div
                      key={i}
                      className="w-3 h-3 rounded-sm border"
                      style={{ backgroundColor: getIntensityColor(intensity) }}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">High</span>
              </div>
            </div>

            {/* Heatmap Grid */}
            <div className="overflow-x-auto">
              <div className="min-w-[600px]">
                {/* Hour labels */}
                <div className="flex mb-2">
                  <div className="w-12"></div> {/* Space for day labels */}
                  {HOURS.map(hour => (
                    <div key={hour} className="flex-1 text-center text-xs text-muted-foreground min-w-[20px]">
                      {hour === 0 ? '12a' : hour === 12 ? '12p' : hour > 12 ? `${hour - 12}p` : `${hour}a`}
                    </div>
                  ))}
                </div>

                {/* Heatmap rows */}
                {DAYS.map(day => (
                  <div key={day} className="flex items-center mb-1">
                    <div className="w-12 text-xs font-medium text-muted-foreground">{day}</div>
                    {HOURS.map(hour => {
                      const dataPoint = heatmapData.find(d => d.day === day && d.hour === hour)
                      const intensity = dataPoint?.intensity || 0
                      const requests = dataPoint?.requests || 0
                      
                      return (
                        <div
                          key={`${day}-${hour}`}
                          className="flex-1 h-6 border border-gray-200 cursor-pointer hover:border-gray-400 transition-colors min-w-[20px] group relative"
                          style={{ backgroundColor: getIntensityColor(intensity) }}
                          title={`${day} ${hour}:00 - ${requests} requests`}
                        >
                          {/* Tooltip on hover */}
                          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap">
                            {day} {hour}:00<br/>{requests} requests
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Usage Insights</span>
          </CardTitle>
          <CardDescription>Key patterns and statistics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Summary Stats */}
            <div>
              <h4 className="font-medium mb-3">Summary</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Requests</span>
                  <span className="font-medium">{getTotalRequests().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Peak Time</span>
                  <span className="font-medium">{getPeakHour()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Avg/Hour</span>
                  <span className="font-medium">
                    {Math.round(getTotalRequests() / (DAYS.length * HOURS.length)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Busiest Days */}
            <div>
              <h4 className="font-medium mb-3 flex items-center space-x-2">
                <Calendar className="h-4 w-4" />
                <span>Busiest Days</span>
              </h4>
              <div className="space-y-2">
                {getAverageByDay().slice(0, 3).map((dayData, index) => (
                  <div key={dayData.day} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full">
                        <span className="text-xs font-medium text-blue-600">#{index + 1}</span>
                      </div>
                      <span className="text-sm font-medium">{dayData.day}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {dayData.total.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Peak Hours */}
            <div>
              <h4 className="font-medium mb-3 flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>Peak Hours</span>
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Business Hours</span>
                  <Badge variant="outline">9AM - 5PM</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Lunch Peak</span>
                  <Badge variant="outline">12PM - 1PM</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Low Traffic</span>
                  <Badge variant="outline">11PM - 5AM</Badge>
                </div>
              </div>
            </div>

            {/* Traffic Patterns */}
            <div>
              <h4 className="font-medium mb-3">Traffic Patterns</h4>
              <div className="space-y-2">
                {[
                  { pattern: 'Weekday Focus', description: 'Higher Mon-Fri traffic' },
                  { pattern: 'Business Hours', description: 'Peak during work hours' },
                  { pattern: 'Weekend Dip', description: 'Lower weekend activity' }
                ].map((item, index) => (
                  <div key={index} className="p-2 bg-gray-50 rounded text-sm">
                    <div className="font-medium">{item.pattern}</div>
                    <div className="text-muted-foreground text-xs">{item.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 